import React from 'react';
import { Box, Typography, IconButton, Tooltip } from '@mui/material';
import { ReactComponent as Edit } from '../../../../assets/SVG/edit icon.svg';
import { ReactComponent as Remove } from '../../../../assets/SVG/delete.svg';
import { PaymentHistoryItem } from '../collectPayment.types';
import { formatCurrency } from '../collectPayment.helper';

// Centralized labels for i18n/easy changes
const LABELS = {
  history: 'Payment History',
  edit: 'Edit',
  remove: 'Remove',
  notes: 'Notes',
  mode: 'Mode',
  txnNo: 'Txn No',
  chequeNo: 'Cheque No',
  chequeDate: 'Date',
  bank: 'Bank',
  provider: 'Provider',
};

// Field config by payment method
const METHOD_DETAILS: Record<string, Array<{ key: keyof PaymentHistoryItem, label: string }>> = {
  'Cash Payment': [{ key: 'notes', label: LABELS.notes }],
  Bank: [
    { key: 'paymentMode', label: LABELS.mode },
    { key: 'transactionNumber', label: LABELS.txnNo },
    { key: 'notes', label: LABELS.notes },
  ],
  'Card Payment': [
    { key: 'transactionNumber', label: LABELS.txnNo },
    { key: 'notes', label: LABELS.notes },
  ],
  Cheque: [
    { key: 'chequeNumber', label: LABELS.chequeNo },
    { key: 'chequeDate', label: LABELS.chequeDate },
    { key: 'bankName', label: LABELS.bank },
    { key: 'notes', label: LABELS.notes },
  ],
  UPI: [
    { key: 'upiProvider', label: LABELS.provider },
    { key: 'transactionNumber', label: LABELS.txnNo },
  ],
  Wallet: [{ key: 'notes', label: LABELS.notes }],
  'Package Advance': [{ key: 'notes', label: LABELS.notes }],
};

const getPaymentDetails = (payment: PaymentHistoryItem): string[] => {
  const configs = METHOD_DETAILS[payment.method] || [];
  return configs
    .map(cfg => payment[cfg.key] ? `${cfg.label}: ${payment[cfg.key]}` : '')
    .filter(Boolean);
};

interface PaymentHistoryListProps {
  payments: PaymentHistoryItem[];
  currency?: string;
  onEdit?: (payment: PaymentHistoryItem) => void;
  onDelete?: (paymentId: string | number) => void;
  showActions?: boolean;
}

const PaymentHistoryList: React.FC<PaymentHistoryListProps> = ({
  payments,
  currency = 'USD',
  onEdit,
  onDelete,
  showActions = true,
}) => {
  if (!payments.length) return null;

  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
        {LABELS.history}
      </Typography>
      {payments.map((payment) => {
        const details = getPaymentDetails(payment);

        return (
          <Box
            key={payment.id}
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              backgroundColor: '#fafafa',
              borderRadius: '6px',
              px: 2,
              py: 1.5,
              mb: 1,
            }}
          >
            <Box sx={{ flex: 1 }}>
              {/* Method and Amount */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  mb: details.length > 0 ? 0.5 : 0,
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  {payment.method} :
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {formatCurrency(payment.amount, currency)}
                </Typography>
              </Box>
              {/* Method-specific details */}
              {details.length > 0 && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 0.5 }}>
                  {details.map((detail, idx) => (
                    <Typography key={idx} variant="caption" color="text.secondary" sx={{ fontSize: '12px' }}>
                      {detail}
                    </Typography>
                  ))}
                </Box>
              )}
            </Box>
            {showActions && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 1 }}>
                <Tooltip title={LABELS.edit}>
                  <IconButton
                    size="small"
                    onClick={() => onEdit?.(payment)}
                    sx={{ color: 'text.secondary' }}
                    aria-label={LABELS.edit}
                  >
                    <Edit width={20} height={20} />
                  </IconButton>
                </Tooltip>
                <Tooltip title={LABELS.remove}>
                  <IconButton
                    size="small"
                    onClick={() => onDelete?.(payment.id)}
                    sx={{ color: '#F25B5B' }}
                    aria-label={LABELS.remove}
                  >
                    <Remove width={20} height={20} />
                  </IconButton>
                </Tooltip>
              </Box>
            )}
          </Box>
        );
      })}
    </Box>
  );
};

export default PaymentHistoryList;